import { ConditionalSpanNode as ConditionalSpanSchema } from '@seen/core/tiptap/schema'
import type { Node as PMNode } from '@tiptap/pm/model'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { ConditionalSpanView } from '#src/components/rich-text-editor/conditional-display/ConditionalSpanView.tsx'
import { ownConditionTarget } from '#src/components/rich-text-editor/conditional-display/conditionalWrapKind.ts'

export interface ConditionalSpanOptions {
  // A template outlines each span and marks it with the condition glyph.
  isTemplate: boolean
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    conditionalSpan: {
      // Wraps the selected inline content (or `range`) of one textblock in a
      // new conditional span.
      wrapInConditionalSpan: (range?: {
        from: number
        to: number
      }) => ReturnType
      // Puts the content of the span at `pos` (else the one around the
      // selection) back in place.
      unwrapConditionalSpan: (pos?: number) => ReturnType
    }
  }
}

export const ConditionalSpanNode =
  ConditionalSpanSchema.extend<ConditionalSpanOptions>({
    addOptions() {
      return { isTemplate: false }
    },

    addNodeView() {
      return ReactNodeViewRenderer(ConditionalSpanView, { as: 'span' })
    },

    addCommands() {
      return {
        wrapInConditionalSpan:
          (range) =>
          ({ tr, dispatch }) => {
            const from = range?.from ?? tr.selection.from
            const to = range?.to ?? tr.selection.to
            const $from = tr.doc.resolve(from)
            const $to = tr.doc.resolve(to)
            if (
              from === to ||
              !$from.parent.isTextblock ||
              !$from.sameParent($to)
            ) {
              return false
            }
            // Spans do not nest: text already conditional, or a run holding
            // a span, is configured through that span instead.
            let holdsSpan = false
            tr.doc.nodesBetween(from, to, (node) => {
              holdsSpan ||= node.type === this.type
              return !holdsSpan
            })
            if (holdsSpan || spanAround(tr.doc, from, this.name)) {
              return false
            }
            // One item alone takes its own condition instead.
            if (ownConditionTarget(tr.doc, from, to) !== null) {
              return false
            }
            if (dispatch) {
              const span = this.type.create(
                { id: crypto.randomUUID() },
                tr.doc.slice(from, to).content
              )
              tr.replaceRangeWith(from, to, span)
            }
            return true
          },
        unwrapConditionalSpan:
          (pos) =>
          ({ tr, dispatch }) => {
            const at = pos === undefined ? undefined : tr.doc.nodeAt(pos)
            const found =
              pos !== undefined && at?.type.name === this.name
                ? { node: at, pos }
                : pos === undefined
                  ? spanAround(tr.doc, tr.selection.from, this.name)
                  : undefined
            if (!found) {
              return false
            }
            if (dispatch) {
              tr.replaceWith(
                found.pos,
                found.pos + found.node.nodeSize,
                found.node.content
              )
            }
            return true
          },
      }
    },

    addKeyboardShortcuts() {
      // A span left empty goes with the next Backspace, rather than lingering
      // as an invisible wrapper.
      return {
        Backspace: () => {
          const { selection } = this.editor.state
          const found = spanAround(
            this.editor.state.doc,
            selection.from,
            this.name
          )
          if (!found || !selection.empty || found.node.content.size > 0) {
            return false
          }
          return this.editor.commands.unwrapConditionalSpan(found.pos)
        },
      }
    },
  })

function spanAround(
  doc: PMNode,
  pos: number,
  name: string
): { node: PMNode; pos: number } | undefined {
  const $pos = doc.resolve(pos)
  for (let depth = $pos.depth; depth > 0; depth--) {
    const node = $pos.node(depth)
    if (node.type.name === name) {
      return { node, pos: $pos.before(depth) }
    }
  }
  return undefined
}
